import { DateTime } from 'luxon'

const resolveZone = (userTimezone?: string): string =>
  (userTimezone && String(userTimezone).trim()) || 'UTC'

/** Clock label shown under each message bubble, e.g. "2:30 PM". Empty for an
 * unparseable timestamp so the UI simply omits the time. */
export const formatMessageTime = (
  iso: string,
  userTimezone?: string
): string => {
  const dt = DateTime.fromISO(iso).setZone(resolveZone(userTimezone))
  return dt.isValid ? dt.toFormat('h:mm a') : ''
}

/** Label for the date divider between messages from different calendar days:
 * "Today" / "Yesterday" for the recent two, "EEEE, LLLL d" (e.g.
 * "Friday, June 20") within the current year, and a trailing ", yyyy" for
 * older messages. Calendar days are computed in the user's profile IANA zone. */
export const formatDaySeparator = (
  iso: string,
  userTimezone?: string
): string => {
  const zone = resolveZone(userTimezone)
  const dt = DateTime.fromISO(iso).setZone(zone)
  if (!dt.isValid) return ''
  const now = DateTime.now().setZone(zone)
  const diffDays = now.startOf('day').diff(dt.startOf('day'), 'days').days
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  return dt.year === now.year
    ? dt.toFormat('EEEE, LLLL d')
    : dt.toFormat('EEEE, LLLL d, yyyy')
}

/** Whether two ISO timestamps land on the same calendar day in the user's
 * profile IANA zone — drives where a day separator is inserted in the thread.
 * An unparseable pair counts as the same day so we don't emit a stray divider. */
export const isSameCalendarDay = (
  a: string,
  b: string,
  userTimezone?: string
): boolean => {
  const zone = resolveZone(userTimezone)
  const da = DateTime.fromISO(a).setZone(zone)
  const db = DateTime.fromISO(b).setZone(zone)
  if (!da.isValid || !db.isValid) return true
  return da.hasSame(db, 'day')
}
